import { buildScenarioSteps } from '../domain/scenarios';
import { newId } from '../domain/crypto';
import { SCHEMA_VERSION } from '../domain/constants';
import { workerEndpoint } from './env';
import type { Finding, RunOutput, ScenarioId } from '../domain/types';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

export class CancelledError extends Error {
  constructor() {
    super('La ejecución fue cancelada por el visitante.');
    this.name = 'CancelledError';
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string };
}

async function postJson(path: string, body: unknown, signal?: AbortSignal): Promise<RunOutput> {
  let response: Response;
  try {
    response = await fetch(workerEndpoint(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CancelledError();
    }
    throw new ApiError('No se pudo contactar al worker.', 'DEPENDENCY_UNAVAILABLE', 0);
  }
  const json: unknown = await response.json();
  if (!response.ok) {
    const envelope = json as ErrorEnvelope;
    throw new ApiError(
      envelope.error?.message ?? 'Error desconocido.',
      envelope.error?.code ?? 'INTERNAL_ERROR',
      response.status,
    );
  }
  return json as RunOutput;
}

async function getJson(path: string, signal?: AbortSignal): Promise<RunOutput> {
  let response: Response;
  try {
    response = await fetch(workerEndpoint(path), { method: 'GET', signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CancelledError();
    }
    throw new ApiError('No se pudo contactar al worker.', 'DEPENDENCY_UNAVAILABLE', 0);
  }
  const json: unknown = await response.json();
  if (!response.ok) {
    const envelope = json as ErrorEnvelope;
    throw new ApiError(
      envelope.error?.message ?? 'Error desconocido.',
      envelope.error?.code ?? 'INTERNAL_ERROR',
      response.status,
    );
  }
  return json as RunOutput;
}

export interface ScenarioRunStep {
  label: string;
  eventId: string;
  output: RunOutput;
}

/** Executes every request a scenario needs (1 for most, 2 for duplicate/out-of-order) in order. */
export async function executeScenario(
  scenarioId: ScenarioId,
  signal?: AbortSignal,
): Promise<ScenarioRunStep[]> {
  const steps = await buildScenarioSteps(scenarioId);
  const results: ScenarioRunStep[] = [];
  for (const step of steps) {
    if (signal?.aborted) throw new CancelledError();
    const output = await postJson('/webhooks/demo', step.input, signal);
    const eventId = (step.input.payload as { eventId: string }).eventId;
    results.push({ label: step.label, eventId, output });
  }
  return results;
}

export async function replayFailedEvent(eventId: string, signal?: AbortSignal): Promise<RunOutput> {
  return postJson(`/events/${encodeURIComponent(eventId)}/replay`, undefined, signal);
}

export async function fetchRun(runId: string, signal?: AbortSignal): Promise<RunOutput> {
  return getJson(`/runs/${encodeURIComponent(runId)}`, signal);
}

/** Synthesizes a client-side "cancelled" result — no server round trip needed. */
export function cancelledRunOutput(scenarioId: string): RunOutput {
  const findings: Finding[] = [
    {
      ruleId: 'stage.cancelled',
      severity: 'info',
      message: 'El visitante canceló la ejecución antes de recibir un resultado.',
    },
  ];
  return {
    schemaVersion: SCHEMA_VERSION,
    runId: newId('run'),
    status: 'cancelled',
    summary: 'Ejecución cancelada por el visitante.',
    findings,
    evidence: { rulesVersion: SCHEMA_VERSION, scenarioId },
  };
}
